import React, { useEffect, useState, useRef, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { PUBLISH } from "@/constants/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, CheckCircle2, XCircle, Loader2, Clock, Zap } from "lucide-react";

export default function Publish() {
  const [jobs, setJobs] = useState([]);
  const [currentJob, setCurrentJob] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    const { data } = await api.get("/publish/jobs");
    setJobs(data);
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/publish/jobs/${jobId}`);
        setCurrentJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPublishing(false);
          await loadJobs();
          toast.success(`Publicación completada: ${data.succeeded}/${data.total_users} firmas`);
        }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("Publish poll failed", e);
      }
    }, 1500);
  };

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const doPublish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/publish");
      toast.info("Publicación iniciada");
      const { data: job } = await api.get(`/publish/jobs/${data.job_id}`);
      setCurrentJob(job);
      startPolling(data.job_id);
    } catch (e) {
      setPublishing(false);
      toast.error(e?.response?.data?.detail || "Error al publicar");
    }
  };

  const progress = currentJob && currentJob.total_users > 0
    ? Math.round((currentJob.processed / currentJob.total_users) * 100) : 0;

  const statusBadge = (s) => {
    const map = {
      completed: ["bg-emerald-100 text-emerald-700 border-emerald-200", CheckCircle2, "Completada"],
      failed: ["bg-red-100 text-red-700 border-red-200", XCircle, "Fallida"],
      running: ["bg-orange-100 text-orange-700 border-orange-200", Loader2, "En curso"],
      pending: ["bg-slate-100 text-slate-700 border-slate-200", Clock, "Pendiente"],
    };
    const [cls, Icon, label] = map[s] || map.pending;
    return (
      <Badge className={`${cls} hover:${cls}`}>
        <Icon className={`w-3 h-3 mr-1 ${s === "running" ? "animate-spin" : ""}`} strokeWidth={2} />
        {label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">
            Despliegue
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Publicar firmas</h1>
          <p className="text-sm text-slate-500 mt-1">
            Genera y despliega las firmas para todos los usuarios activos.
          </p>
        </div>
        <Button
          onClick={doPublish} disabled={publishing}
          className="bg-orange-600 hover:bg-orange-700 text-white transition-colors"
          data-testid={PUBLISH.publishBtn}
        >
          <Send className="w-4 h-4 mr-2" strokeWidth={1.75} />
          {publishing ? "Publicando..." : "Publicar ahora"}
        </Button>
      </div>

      <Card className="p-4 border-orange-200 shadow-sm bg-orange-50/40">
        <div className="flex items-start gap-3">
          <Zap className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
          <div className="text-xs text-slate-700 leading-relaxed">
            <strong>Método de despliegue recomendado (2026):</strong> Exchange Online Transport Rules
            (server-side). Microsoft Graph no expone endpoint para firmas; se aplican vía reglas de
            transporte con tokens dinámicos (%%DisplayName%%, %%Title%%). En este MVP, la
            publicación genera las firmas renderizadas y las almacena listas para inyectarse via
            transport rules o Office Add-in. <strong>Los usuarios inactivos son excluidos automáticamente.</strong>
          </div>
        </div>
      </Card>

      {currentJob && (
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Trabajo actual
              </div>
              <div className="text-base font-semibold text-slate-900 mt-0.5">
                {currentJob.processed}/{currentJob.total_users} usuarios procesados
              </div>
            </div>
            {statusBadge(currentJob.status)}
          </div>
          <Progress value={progress} className="h-2 bg-slate-100" data-testid={PUBLISH.progressBar} />
          <div className="grid grid-cols-4 gap-3 mt-4">
            <div className="text-center p-2 rounded-md bg-emerald-50 border border-emerald-200">
              <div className="text-xl font-semibold text-emerald-700">{currentJob.succeeded}</div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-600">Exitosas</div>
            </div>
            <div className="text-center p-2 rounded-md bg-amber-50 border border-amber-200">
              <div className="text-xl font-semibold text-amber-700">{currentJob.skipped || 0}</div>
              <div className="text-[11px] uppercase tracking-wider text-amber-600">Sin regla</div>
            </div>
            <div className="text-center p-2 rounded-md bg-red-50 border border-red-200">
              <div className="text-xl font-semibold text-red-700">{currentJob.failed}</div>
              <div className="text-[11px] uppercase tracking-wider text-red-600">Fallidas</div>
            </div>
            <div className="text-center p-2 rounded-md bg-slate-50 border border-slate-200">
              <div className="text-xl font-semibold text-slate-700">{progress}%</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-600">Progreso</div>
            </div>
          </div>
          {currentJob.log && currentJob.log.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Log
              </div>
              <div className="bg-slate-900 text-slate-100 font-mono text-[11px] p-3 rounded max-h-56 overflow-auto">
                {currentJob.log.slice(-30).map((l, i) => (
                  <div key={`${i}-${l.slice(0, 40)}`} className={l.startsWith("[error]") ? "text-red-400" : l.startsWith("[skip]") ? "text-amber-300" : "text-emerald-300"}>
                    {l}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <Card className="border-slate-200 shadow-sm" data-testid={PUBLISH.jobsList}>
        <div className="p-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Historial de publicaciones</h3>
        </div>
        <div className="divide-y divide-slate-200">
          {jobs.map((j) => (
            <div key={j.id} className="p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors">
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">
                  {new Date(j.started_at).toLocaleString("es")}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Por {j.triggered_by || "sistema"} · {j.succeeded}/{j.total_users} exitosas
                </div>
              </div>
              {statusBadge(j.status)}
            </div>
          ))}
          {jobs.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">Sin publicaciones anteriores.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
